import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import "./Home.css"; // Use the same CSS as home page

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [expandedNames, setExpandedNames] = useState(new Set());
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'all' or 'instock'
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Products per page based on screen size
  const productsPerPage = isMobile ? 10 : 15;

  // Fetch products from Firestore
  const fetchProducts = async () => {
    try {
      const q = query(
        collection(db, "products"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    
    // Add resize listener to detect screen size changes
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 769;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        setCurrentPage(1); // Reset to first page when screen size changes
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.custom-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isDropdownOpen]);

  // Filter products based on availability
  const filteredProducts = availabilityFilter === 'instock' 
    ? products.filter(product => product.availability === 'In stock' || !product.availability)
    : products;

  // Calculate pagination values
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  const handleFilterChange = (e) => {
    setAvailabilityFilter(e.target.value);
    setCurrentPage(1);
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page === currentPage || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Smooth scroll to show navbar and page header
    const scrollToTop = () => {
      const pageHeader = document.querySelector('.latest-header-white');
      const navbar = document.querySelector('.navbar') || document.querySelector('nav');
      
      if (pageHeader) {
        // Calculate offset to show navbar + some padding
        const navbarHeight = navbar ? navbar.offsetHeight : 0;
        const offset = navbarHeight + 20; // Add 20px padding
        const elementPosition = pageHeader.offsetTop - offset;
        
        window.scrollTo({ 
          top: Math.max(0, elementPosition), 
          behavior: 'smooth' 
        });
      } else {
        // Fallback to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    scrollToTop();
    
    // Add a small delay for smooth transition effect
    setTimeout(() => {
      setCurrentPage(page);
      setIsTransitioning(false);
    }, 300);
  };

  // Toggle product name expansion
  const toggleNameExpansion = (productId, event) => {
    event.stopPropagation(); // Prevent card click from triggering
    setExpandedNames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Check if text is truncated (more than 2 lines)
  const isTextTruncated = (text) => {
    const wordsPerLine = isMobile ? 3 : 4; // Approximate words per line
    const words = text.split(' ');
    return words.length > (wordsPerLine * 2); // More than 2 lines worth
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Header */}
      <div className="latest-header-white">
        <h3 className="latest-title">All Products</h3>
        
        {/* Filter Dropdown */}
        <div className="product-filter-container">
          <label className="filter-label">Filter</label>
          <div className="custom-dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <div className="dropdown-selected">
              {availabilityFilter === 'all' ? 'All' : 'In Stock'}
              <span className="dropdown-arrow">{isDropdownOpen ? '▲' : '▼'}</span>
            </div>
            {isDropdownOpen && (
              <div className="dropdown-options">
                <div 
                  className={`dropdown-option ${availabilityFilter === 'all' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange({ target: { value: 'all' } });
                    setIsDropdownOpen(false);
                  }}
                >
                  All
                </div>
                <div 
                  className={`dropdown-option ${availabilityFilter === 'instock' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange({ target: { value: 'instock' } });
                    setIsDropdownOpen(false);
                  }}
                >
                  In Stock
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {products.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
          <p>No products available at the moment. Check back soon!</p>
        </div>
      ) : (
        <>
          <div className="latest-section">
            <div className={`product-grid-mobile ${isTransitioning ? 'transitioning' : ''}`}>
              {currentProducts.map((product) => (
                <div className="product-card-mobile" key={product.id}>
                  <div className="product-image-container">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="product-img-mobile"
                    />
                  </div>
                  <div className="product-details">
                    <h4 
                      className={`product-name-mobile ${
                        expandedNames.has(product.id) 
                          ? 'expanded' 
                          : isTextTruncated(product.name) 
                            ? 'truncated' 
                            : ''
                      }`}
                      onClick={(e) => isTextTruncated(product.name) && toggleNameExpansion(product.id, e)}
                      style={{ 
                        cursor: isTextTruncated(product.name) ? 'pointer' : 'default'
                      }}
                      title={isTextTruncated(product.name) ? "Click to expand/collapse" : ""}
                    >
                      {product.name}
                    </h4>
                    <p className="product-price-mobile">{product.price}</p>
                    
                    {/* Status */}
                    <div className="availability-container">
                      <span className="availability-label">Status</span>
                      <span className={`availability-status ${(product.availability === 'In stock' || !product.availability) ? 'in-stock' : 'sold-out'}`}>
                        {(product.availability === 'In stock' || !product.availability) ? 'In stock' : 'Sold out'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-controls">
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`page-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
